import React, { useState } from 'react';
import { BsChevronDown, BsFolder, BsFileEarmarkText, BsClockHistory, BsFolder2Open, BsFolder2 } from 'react-icons/bs';

const TreeView = ({ categories = [], parentId = 0, updateSelectedCategory, getLatestArticles }) => {
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);

    const categoryHasChildren = (categoryId) => {
        return categories.some(category => category.parentCategoryId === categoryId);
    };

    const handleCategoryToggle = (categoryId, categoryTitle, hasChildren, isLatest = false) => {
        setSelectedCategoryId(categoryId);

        // Only trigger update if leaf node or Latest Article
        if (!hasChildren || isLatest) {
            if (isLatest) {
                getLatestArticles();
            } else {
                updateSelectedCategory(categoryId, categoryTitle);
            }
        }

        // Expand/collapse for parent nodes
        if (hasChildren) {
            if (expandedCategories.includes(categoryId)) {
                setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
            } else {
                setExpandedCategories([...expandedCategories, categoryId]);
            }
        }
    };

    const renderCategories = (parentId, depth = 0) => {
        return categories
            .filter(category => category.parentCategoryId === parentId)
            .map(category => {
                const hasChildren = categoryHasChildren(category.id);
                const isExpanded = expandedCategories.includes(category.id);
                const isSelected = selectedCategoryId === category.id;

                return (
                    <div key={category.id} className="tree-item-wrapper">
                        <div
                            className={`tree-item ${isSelected ? 'selected' : ''} ${hasChildren ? 'has-children' : ''}`}
                            style={{ '--depth': 0 }}
                        >
                            <button
                                className="tree-item-button btn d-flex align-items-center w-100 text-start border-0 rounded-3"
                                onClick={() =>
                                    handleCategoryToggle(category.id, category.title, hasChildren)
                                }
                            >
                                <div className="d-flex align-items-center flex-grow-1 min-width-0">
                                    {hasChildren && (
                                        <span className={`tree-toggle-icon transition-150 me-1 ${isExpanded ? 'expanded' : ''}`}>
                                            <BsChevronDown size={12} />
                                        </span>
                                    )}
                                    {!hasChildren && <span className="" />}

                                    <span className="tree-item-icon me-2 flex-shrink-0">
                                        {hasChildren ? (
                                            isExpanded ? (
                                                <BsFolder2Open size={18} />
                                            ) : (
                                                <BsFolder2 size={18} />
                                            )
                                        ) : (
                                            <BsFileEarmarkText size={16} />
                                        )}
                                    </span>

                                    <span className="tree-item-label text-wrap" style={{ lineHeight: '1.3' }}>
                                        {category.title}
                                    </span>
                                </div>

                                {hasChildren && (
                                    <span className="badge bg-light text-dark fs-10px flex-shrink-0 ms-2">
                                        {categories.filter(cat => cat.parentCategoryId === category.id).length}
                                    </span>
                                )}
                            </button>
                        </div>

                        {isExpanded && hasChildren && (
                            <div className="tree-children-container">
                                {renderCategories(category.id, depth + 1)}
                            </div>
                        )}
                    </div>
                );
            });
    };

    return (
        <div className="tree-view-container bg-white rounded-4 shadow-sm border-0">
            <div className="tree-view-header border-bottom bg-light bg-opacity-50 rounded-top-4 p-3">
                <div className="d-flex align-items-center">
                    <i className="bi bi-diagram-3-fill text-primary me-2"></i>
                    <h6 className="tree-view-title mb-0 fw-bold text-dark">Categories</h6>
                </div>
            </div>

            <div className="tree-view-body p-3">
                {/* Latest Article as a proper tree node */}
                <div className="tree-item-wrapper mb-2">
                    <div className={`tree-item ${selectedCategoryId === 'latest' ? 'selected' : ''}`}>
                        <button
                            className="tree-item-button btn d-flex align-items-center w-100 text-start border-0 rounded-3"
                            onClick={() => handleCategoryToggle('latest', 'Latest Article', false, true)}
                        >
                            <div className="d-flex align-items-center flex-grow-1">
                                <span className="tree-toggle-spacer me-2" />
                                <span className="tree-item-icon me-2">
                                    <BsClockHistory size={16} />
                                </span>
                                <span className="tree-item-label fw-semibold">Latest Articles</span>
                            </div>
                            <i className="bi bi-arrow-right-short text-muted flex-shrink-0"></i>
                        </button>
                    </div>
                </div>

                {/* Render the categories */}
                {categories.length > 0 ? renderCategories(parentId) : (
                    <div className="tree-view-empty text-center py-4">
                        <i className="bi bi-inbox display-4 text-muted mb-2 d-block"></i>
                        <p className="text-muted mb-0">No categories available</p>
                    </div>
                )}
            </div>

            <style jsx>{`
                .tree-view-container {
                    font-family: 'Segoe UI', system-ui, sans-serif;
                }

                .tree-item-wrapper {
                    margin-bottom: 0.25rem;
                }

                .tree-item {
                    transition: all 0.2s ease;
                    margin-left: calc(var(--depth) * 0.15rem);
                }

                .tree-item-button {
                    padding: 0.625rem 0.75rem;
                    background: transparent;
                    color: var(--bs-body-color);
                    transition: all 0.2s ease;
                    font-size: 0.875rem;
                    min-height: 44px;
                    border: 1px solid transparent !important;
                }

                .tree-item-button:hover {
                    background: linear-gradient(135deg, var(--bs-primary-bg-subtle) 0%, #f0f8ff 100%);
                    color: var(--bs-primary);
                    border-color: var(--bs-primary-border-subtle) !important;
                    transform: translateX(2px);
                }

                .tree-item.selected .tree-item-button {
                    background: linear-gradient(135deg, var(--bs-primary-bg-subtle) 0%, #e3f2fd 100%);
                    color: var(--bs-primary);
                    border-color: var(--bs-primary) !important;
                    box-shadow: 0 2px 12px rgba(var(--bs-primary-rgb), 0.15);
                    font-weight: 600;
                }

                /* Ensure all icons and text remain visible in selected state */
                .tree-item.selected .tree-item-button,
                .tree-item.selected .tree-item-label,
                .tree-item.selected .tree-item-icon,
                .tree-item.selected .tree-toggle-icon,
                .tree-item.selected .bi-arrow-right-short {
                    color: var(--bs-primary) !important;
                }

                .tree-item.selected .badge {
                    background: var(--bs-primary) !important;
                    color: white !important;
                }

                .tree-toggle-icon {
                    transition: all 0.15s ease;
                    display: inline-flex;
                    align-items: center;
                    width: 16px;
                    flex-shrink: 0;
                    color: var(--bs-secondary);
                }

                .tree-toggle-icon.expanded {
                    transform: rotate(0deg);
                }

                .tree-toggle-icon:not(.expanded) {
                    transform: rotate(-90deg);
                }

                .tree-toggle-spacer {
                    display: inline-block;
                    width: 16px;
                    flex-shrink: 0;
                }

                .tree-item-icon {
                    display: inline-flex;
                    align-items: center;
                    width: 20px;
                    flex-shrink: 0;
                    color: var(--bs-warning);
                }

                .tree-item-icon .text-primary {
                    color: var(--bs-primary) !important;
                }

                .tree-item-label {
                    flex: 1;
                    min-width: 0;
                    font-weight: 500;
                    line-height: 1.3;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    white-space: normal;
                    text-align: left;
                    color: inherit;
                }

                .tree-children-container {
                    border-left: 2px solid var(--bs-light);
                    margin-left: 0.5rem;
                    padding-left: 0.75rem;
                    transition: all 0.3s ease;
                }

                .tree-children-container:hover {
                    border-left-color: var(--bs-primary-border-subtle);
                }

                .fs-10px {
                    font-size: 0.625rem;
                }

                .transition-150 {
                    transition: all 0.15s ease;
                }

                .min-width-0 {
                    min-width: 0;
                }

                /* Smooth animations */
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .tree-children-container {
                    animation: slideDown 0.2s ease;
                }

                /* Custom scrollbar for deep trees */
                .tree-view-body {
                    max-height: 70vh;
                    overflow-y: auto;
                }

                .tree-view-body::-webkit-scrollbar {
                    width: 6px;
                }

                .tree-view-body::-webkit-scrollbar-track {
                    background: var(--bs-light);
                    border-radius: 3px;
                }

                .tree-view-body::-webkit-scrollbar-thumb {
                    background: var(--bs-secondary);
                    border-radius: 3px;
                }

                .tree-view-body::-webkit-scrollbar-thumb:hover {
                    background: var(--bs-primary);
                }

                /* Enhanced text wrapping for long category names */
                .tree-item-button {
                    align-items: flex-start;
                }

                .tree-item-button .d-flex {
                    align-items: flex-start;
                }

                /* Responsive design */
                @media (max-width: 768px) {
                    .tree-view-body {
                        padding: 1rem !important;
                    }

                    .tree-item-button {
                        padding: 0.75rem 0.75rem;
                        font-size: 0.9rem;
                        min-height: 48px;
                    }

                    .tree-item {
                        margin-left: calc(var(--depth) * 0.75rem);
                    }

                    .tree-item-label {
                        font-size: 0.9rem;
                        line-height: 1.4;
                    }
                }

                @media (max-width: 576px) {
                    .tree-view-container {
                        border-radius: 1rem !important;
                    }

                    .tree-item-label {
                        font-size: 0.85rem;
                        line-height: 1.3;
                    }

                    .badge {
                        display: none;
                    }

                    .tree-item-button {
                        padding: 0.875rem 0.75rem;
                        min-height: 52px;
                    }
                }

                /* Ensure proper vertical alignment for multi-line text */
                .tree-item-button .d-flex.align-items-center {
                    align-items: flex-start !important;
                }

                .tree-item-icon {
                    margin-top: 0.1rem;
                }

                .tree-toggle-icon,
                .tree-toggle-spacer {
                    margin-top: 0.1rem;
                }
            `}</style>
        </div>
    );
};

export default TreeView;